/**
 * CreatorRestingBanner — the creator-side half of the dark state
 * (TRIAL_PLAN.md §8.10). Shown wherever a lapsed creator actually lands, which
 * is the Fan Portal: `has_dashboard_access()` routes them out of the dashboard
 * the moment their subscription ends, so without this they get no explanation
 * for the vanished dashboard and no route back.
 *
 * Oct 2026 (Randy — "why am I still seeing this giant card") — two changes:
 *
 *   1. It requires a silo. `dark_state` only asks whether the account ever
 *      HELD access, and a comp or a used trial counts as evidence, so 13
 *      preview accounts owning nothing were being told their silos were
 *      resting. Every noun in the reassurance line was empty for them.
 *   2. It is a closed chevron row, not a card. Randy: *"an alert that is
 *      chevroned, could have a small header, but is closed by default, can be
 *      opened and read. Can be dismissed for the browser session. But may
 *      return the next time they load in."* Dismissal is `sessionStorage`,
 *      which is exactly that contract — gone for this tab, back on the next
 *      visit. It also stands down entirely while the comp banner is already
 *      offering the same Stripe checkout above it.
 */
import { useState } from "react";
import { Moon, CaretRight, X, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useEntitlements } from "@/lib/entitlements";

const DISMISS_KEY = "mysilo.resting.dismissed";

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return null; }
};

const readDismissed = () => {
  try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
};

export default function CreatorRestingBanner() {
  const { ent } = useEntitlements();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(readDismissed);

  const dark = ent?.dark;
  if (!dark?.dark) return null;
  if (!(ent?.silo_count > 0)) return null;
  if (ent?.comp_alert_active) return null;
  if (dismissed) return null;

  // The way back is the subscription itself — same checkout the portal's
  // creator upgrade uses. Account Settings has no "resubscribe" control, so
  // sending them there would have been a dead end.
  const reactivate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/stripe/platform/checkout", { origin_url: window.location.origin, return_path: window.location.pathname });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  const dismiss = (e) => {
    e.stopPropagation();
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* private mode */ }
    setDismissed(true);
  };

  const until = fmtDate(dark.grace_until);
  return (
    <div
      className="rounded-lg border border-[#6B6A68]/25 bg-[#F0F1EE] mt-6 overflow-hidden"
      data-testid="creator-resting-banner"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid="creator-resting-toggle"
          className="group flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          <CaretRight
            size={13}
            weight="bold"
            className={`shrink-0 text-[#6B6A68] transition-transform duration-200 ${open ? "rotate-90" : ""}`}
          />
          <Moon size={14} weight="duotone" className="shrink-0 text-[#4A5D4E]" />
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#6B6A68] truncate group-hover:text-[#1C1B1A] transition-colors">
            Your silos are resting
          </span>
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss until next visit"
          data-testid="creator-resting-dismiss"
          className="shrink-0 p-1 rounded text-[#6B6A68] hover:text-[#1C1B1A] hover:bg-[#6B6A68]/10 transition-colors"
        >
          <X size={12} weight="bold" />
        </button>
      </div>

      {open && (
        <div className="px-3 pb-3 pl-[42px]" data-testid="creator-resting-detail">
          <p className="text-sm text-[#1C1B1A]">
            {dark.public_dark
              ? "Public answers are paused and fan billing is paused — nobody is being charged for a silo that isn't answering."
              : `Fan answers keep running${until ? ` until ${until}` : ""} while your credit lasts, then the public side rests. Fan billing is paused, not cancelled.`}
          </p>
          <p className="text-[12px] text-[#4A5D4E] mt-1.5">
            <strong>Nothing has been deleted.</strong> Your silos, your library and every answer
            your twin wrote are exactly where you left them — reactivating brings it all back at once.
          </p>
          <button
            type="button"
            onClick={reactivate}
            disabled={busy}
            data-testid="creator-resting-reactivate"
            className="mt-3 inline-flex items-center gap-2 text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] transition-colors disabled:opacity-60"
          >
            {busy ? "Opening checkout…" : <>Turn my silos back on <ArrowRight size={13} /></>}
          </button>
        </div>
      )}
    </div>
  );
}
